import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { Button } from './Button';
import { ThemedText } from './ThemedText';
import pdfReportService from '../api/services/pdfReportService';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${p => p.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: ${p => p.theme.spacing.md};
`;

const Modal = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Section = styled.div`
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const CopyRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.small};
  cursor: text;

  &:read-only {
    cursor: default;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.border};
  margin: ${p => p.theme.spacing.lg} 0;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.$error ? p.theme.colors.error : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.$error ? p.theme.colors.error : p.theme.colors.tint};
  }
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: ${p => p.theme.typography.sizes.default};
  resize: vertical;
  min-height: 60px;
  max-height: 150px;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const FieldGroup = styled.div`
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.error};
  margin-top: 4px;
`;

const EmailFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.md};
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ShareReportModal = ({ isOpen, reportId, onClose }) => {
  const theme = useTheme();

  const [publicUrl, setPublicUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch public URL on open
  useEffect(() => {
    if (isOpen && reportId) {
      setPublicUrl('');
      setCopied(false);
      setEmail('');
      setEmailError('');
      setNote('');
      setSending(false);
      setUrlLoading(true);

      pdfReportService.getPublicUrl(reportId)
        .then(result => {
          setPublicUrl(result?.publicUrl || result?.url || '');
        })
        .catch(() => {
          toast.error('Failed to get share link');
        })
        .finally(() => setUrlLoading(false));
    }
  }, [isOpen, reportId]);

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSendEmail = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Email is required');
      return;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError('');
    setSending(true);

    try {
      await pdfReportService.shareReport(reportId, trimmedEmail, note.trim() || null);
      toast.success(`Report shared with ${trimmedEmail}`);
      onClose();
    } catch (error) {
      const msg = error?.response?.data || error?.message || 'Failed to share report';
      toast.error(typeof msg === 'string' ? msg : 'Failed to share report');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} theme={theme} onClick={onClose}>
      <Modal theme={theme} onClick={e => e.stopPropagation()}>
        <Header theme={theme}>
          <ThemedText type="subtitle">Share Report</ThemedText>
          <Button variant="ghost" size="small" onClick={onClose}>X</Button>
        </Header>

        <Section theme={theme}>
          <SectionLabel theme={theme}>Copy Link</SectionLabel>
          <CopyRow theme={theme}>
            <UrlInput
              theme={theme}
              readOnly
              value={urlLoading ? 'Loading...' : publicUrl}
              onClick={e => e.target.select()}
            />
            <Button
              variant="secondary"
              size="small"
              onClick={handleCopy}
              disabled={urlLoading || !publicUrl}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </CopyRow>
        </Section>

        <Divider theme={theme} />

        <Section theme={theme}>
          <SectionLabel theme={theme}>Email Report</SectionLabel>

          <FieldGroup theme={theme}>
            <FieldLabel theme={theme}>Recipient Email *</FieldLabel>
            <EmailInput
              theme={theme}
              $error={!!emailError}
              type="email"
              placeholder="recipient@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(''); }}
            />
            {emailError && <ErrorText theme={theme}>{emailError}</ErrorText>}
          </FieldGroup>

          <FieldGroup theme={theme}>
            <FieldLabel theme={theme}>Note (optional)</FieldLabel>
            <NoteTextarea
              theme={theme}
              placeholder="Add a message..."
              value={note}
              maxLength={1000}
              onChange={e => setNote(e.target.value)}
            />
          </FieldGroup>

          <EmailFooter theme={theme}>
            <Button
              variant="primary"
              size="small"
              onClick={handleSendEmail}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </EmailFooter>
        </Section>
      </Modal>
    </Overlay>
  );
};
